import * as Path from "./path";
import {UrlVarService} from "./url";


export interface IConfigurationIndex {
    [key: string]: any
}

export interface IConfiguration {
    aiccAuFilename(): string;
    aiccCourseFilename(): string;
    aiccDescriptorFilename(): string;
    aiccEndpoint(): string;
    aiccLoadRelativeToAu(): boolean;
    aiccRawAu(): string;
    aiccRawCourse(): string;
    aiccRawDescriptor(): string;
    aiccRawStructure(): string;
    aiccSessionId(systemId: string): string;
    aiccStructureFilename(): string;
    autoLaunchFirstSco(): boolean;
    closePopupSingleScoBehavior(): string;
    closePopupMultiScoBehavior(): string;
    closePopupSingleScoCustomFunction(): any;
    closePopupMultiScoCustomFunction(): any;
    cmi5ManifestFilename(): string;
    commentsFromLMS(scoId: string): object[];
    courseBaseHtmlPath(): string;
    courseDirectory(): string;
    courseId(): string;
    logMessageBehavior(): string;
    logMessageCustomFunction(): any;
    exitAction(): string;
    exitFn(): Function;
    exitUrl(): string;
    frameDefaultUrl(): string;
    frameId(): string;
    frameInterstitialUrl(): string;
    getCredit(): string;
    getDataAjaxMethod(): string;
    getDataHeaders(): any;
    getDataUrl(): string;
    getMode(): string;
    initializeScoAtOpen(): boolean;
    launchDataAllowOverride(scoId: string): boolean;
    launchDataOverride(scoId: string): string;
    layoutUpdater(): Function;
    learningSpecification(): string;
    looseIdentifiers(): boolean;
    masteryScoreMode(): string;
    modeBrowseAllowSave(): boolean;
    modeReviewAllowSave(): boolean;
    multiScoView(): string;
    navigationType(): string;
    openContentIn(): string;
    popupMainContentMessageAfterOpen(): string;
    popupMainContentMessageFailed(): string;
    popupName(): string;
    popupWindowParams(): string;
    reenterActivity(): boolean;
    returnToLastActivityOnLaunch(): boolean;
    saveDataOnCommit(): boolean;
    saveDataOnSetValue(): boolean;
    scoreAllowChangeAfterCompleted(): boolean;
    scoreAllowChangeAfterFailed(): boolean;
    scoreAllowChangeAfterPassed(): boolean;
    scoreAllowReduce(): boolean;
    scoreConvertNormalizedToRawMethod(): string;
    scoreConvertRawToNormalizedMethod(): string;
    scoreRollupAction(): string;
    scoreRollupCustomFunction(): any;
    scoreRollupIncludeScosWithNoScore(): boolean;
    scorm2004Mode(): string;
    scormManifestFilename(): string;
    scormManifestRawXml(): string;
    scormManifestUseRawXml(): boolean;
    startIncomplete(): boolean;
    setDataAjaxMethod(): string;
    setDataHeaders(): any;
    setDataUrl(): string;
    singleScoView(): string;
    statusAllowChangeAfterCompleted(): boolean;
    statusAllowChangeAfterFailed(): boolean;
    statusAllowChangeAfterPassed(): boolean;
    statusAllowCompletionOnUnsetFinish(): boolean;
    statusRollupAction(): any;
    statusRollupCustomFunction(): any;
    statusVisualOverride(): string;
    statusVisualOverrideFn(): Function;
    storageAdapter(): any;
    storageAllowCORS(): boolean;
    storageCallbackAfterLoad(): Function;
    storageCallbackAfterSave(): Function;
    storageGzipRequestBody(): boolean;
    storageIncludeScoDetails(): boolean;
    storageQueueSaves(): boolean;
    storageSaveDuplicateCommits(): boolean;
    storageSaveFrequency(): number;
    storageTimeout(): number;
    storageUseFetch(): boolean;
    storageUseSaveWorker(): boolean;
    storageUseWithCredentials(): boolean;
    studentId(): string;
    studentName(): string;
    validateDataModelLength(): boolean;
    xapiActor(): string;
    xapiAuth(): string;
    xapiEndpoint(): string;
    xapiManifestFilename(): string;
    xapiRegistration(): string;
}


class BasicConfiguration implements IConfiguration, IConfigurationIndex {
    aiccAuFilename(): string {
        return "course.au";
    }

    aiccCourseFilename(): string {
        return "course.crs";
    }

    aiccDescriptorFilename(): string {
        return "course.des";
    }

    aiccEndpoint(): string {
        return "";
    }

    aiccLoadRelativeToAu(): boolean {
        return true;
    }

    aiccRawAu(): string {
        return "";
    }

    aiccRawCourse(): string {
        return "";
    }

    aiccRawDescriptor(): string {
        return "";
    }

    aiccRawStructure(): string {
        return "";
    }

    aiccSessionId(systemId: string): string {
        return "SESSION_ID_HERE";
    }

    aiccStructureFilename(): string {
        return "course.cst";
    }

    autoLaunchFirstSco(): boolean {
        return true;
    }

    closePopupSingleScoBehavior(): string {
        return "exit";
    }

    closePopupMultiScoBehavior(): string {
        return "";
    }

    closePopupSingleScoCustomFunction(): any {
        return null;
    }

    closePopupMultiScoCustomFunction(): any {
        return null;
    }

    cmi5ManifestFilename(): string {
        return "cmi5.xml";
    }

    courseBaseHtmlPath(): string {
        return "";
    }

    commentsFromLMS(sco: string): object[] {
        return [];
    }

    courseDirectory(): string {
        if (UrlVarService.get("courseDirectory").charAt(0) === "/") {
            return UrlVarService.get("courseDirectory");
        }
        return Path.join([this.courseBaseHtmlPath(), UrlVarService.get("courseDirectory")]);
    }

    courseId(): string {
        return UrlVarService.get("courseId");
    }

    /**
     * Valid options:
     * "none": Do nothing.  Useful for external management of unload behavior.
     * "close": Attempt to close the current window.
     * "referrer": Redirect to the referrer URL.
     * "url": Redirect to a URL.
     * "custom": Call a custom exit function, defined by exitFn().
     * @returns {string}
     */
    exitAction(): string {
        return "referrer";
    }

    exitFn(): Function {
        return null;
    }

    exitUrl(): string {
        return "blank_unloading.htm";
    }

    frameDefaultUrl(): string {
        return "blank.htm";
    }

    frameId(): string {
        return "jca_course_player";
    }

    frameInterstitialUrl(): string {
        return "int.htm";
    }

    getCredit(): string {
        return UrlVarService.get("credit");
    }

    getDataAjaxMethod(): string {
        return "POST";
    }

    getDataHeaders(): any {
        return {};
    }

    getDataUrl(): string {
        return "/trackAllInOne/get";
    }

    getMode(): string {
        return UrlVarService.get("mode");
    }

    initializeScoAtOpen(): boolean {
        return false;
    }

    launchDataAllowOverride(scoId: string): boolean {
        return false;
    }

    launchDataOverride(scoId: string): string {
        return "";
    }

    /**
     * Provide a custom function to perform layout updates based on the system state, including information about the
     * course itself (single SCO vs. multi SCO, for example).
     * Expected function signature is single argument that can take an SSLA object.
     * @returns {null}
     */
    layoutUpdater(): Function {
        return null;
    }

    learningSpecification(): string {
        return "";
    }

    logMessageBehavior(): string {
        return "alertErrorOnly";
    }

    logMessageCustomFunction(): any {
        return null;
    }

    looseIdentifiers(): boolean {
        return false;
    }


    /**
     * Valid options:
     * "none" or "": ignore the mastery score altogether
     * "always": apply mastery score on finish if it exists, in all cases where a cmi.core.score.raw is set to an actual value. (edited)
     * "after_completion" (default): apply mastery score on finish only if cmi.core.lesson_status is “completed” (or the equivalent version of this in SCORM 2004)
     */
    masteryScoreMode(): string {
        return "after_completion";
    }

    modeBrowseAllowSave(): boolean {
        return false;
    }

    modeReviewAllowSave(): boolean {
        return true;
    }

    /**
     * Valid options:
     *   "": Leave it however it's currently displaying.
     *   "HIDE_ALL": Hide all non-course view components.
     *   "HIDE_NAV_BUTTONS": Hide the navigation header.
     *   "HIDE_TREE": Hide the navigation tree.
     * @returns {string}
     */
    multiScoView(): string {
        return "";
    }

    navigationType(): string {
        return "";
    }

    openContentIn(): string {
        return "inline";
    }

    popupMainContentMessageAfterOpen(): string {
        return 'Your course has been opened in a popup.  When you have completed your course, please make another selection from the tree, press exit or close this window.';
    }

    popupMainContentMessageFailed(): string {
        return 'Your course window did not pop open correctly.<br>  <a onclick="parent.ssla.ssla.popupManually()" href="#">Click here to open the course.</a>';
    }

    popupName(): string {
        return "sco_win";
    }

    popupWindowParams(): string {
        return "status=1,toolbar=1,scrollbars=1,resizable=1,alwaysRaised=1";
    }

    /**
     * Allow re-entrance to an activity after it has been initialized and before it has been terminated.
     * @returns {boolean}
     */
    reenterActivity(): boolean {
        return true;
    }

    returnToLastActivityOnLaunch(): boolean {
        return false;
    }

    saveDataOnCommit(): boolean {
        return true;
    }

    saveDataOnSetValue(): boolean {
        return false;
    }

    scoreAllowChangeAfterCompleted(): boolean {
        return true;
    }

    scoreAllowChangeAfterFailed(): boolean {
        return true;
    }

    scoreAllowChangeAfterPassed(): boolean {
        return true;
    }

    scoreAllowReduce(): boolean {
        return true;
    }

    scoreConvertNormalizedToRawMethod(): string {
        return "same_sign";
    }

    scoreConvertRawToNormalizedMethod(): string {
        return "same_sign";
    }

    scoreRollupAction(): string {
        return "";
    }

    scoreRollupCustomFunction(): any {
        return null;
    }

    scoreRollupIncludeScosWithNoScore(): boolean {
        return false;
    }

    scorm2004Mode(): string {
        return "simple";
    }

    scormManifestFilename(): string {
        return "imsmanifest.xml";
    }

    scormManifestRawXml(): string {
        return "";
    }

    /**
     * If true, use scormManifestRawXml for the manifest XML body.
     * If false, load the XML from the file referenced by scormManifestFilename.
     * @returns {boolean}
     */
    scormManifestUseRawXml(): boolean {
        return false;
    }

    setDataAjaxMethod(): string {
        return "POST";
    }

    setDataHeaders(): any {
        return {};
    }

    setDataUrl(): string {
        return "/trackAllInOne/set";
    }

    /**
     * Valid options:
     *   "": Leave it however it's currently displaying.
     *   "HIDE_ALL": Hide all non-course view components.
     *   "HIDE_NAV_BUTTONS": Hide the navigation header.
     *   "HIDE_TREE": Hide the navigation tree.
     * @returns {string}
     */
    singleScoView(): string {
        return "";
    }

    /**
     * If true, default a lesson to "incomplete" instead of "not attempted".
     * @returns {boolean}
     */
    startIncomplete(): boolean {
        return false;
    }

    statusAllowChangeAfterCompleted(): boolean {
        return true;
    }

    statusAllowChangeAfterFailed(): boolean {
        return true;
    }

    statusAllowChangeAfterPassed(): boolean {
        return true;
    }

    statusAllowCompletionOnUnsetFinish(): boolean {
        return true;
    }

    statusRollupAction(): string {
        return "";
    }

    statusRollupCustomFunction(): any {
        return null;
    }

    /**
     * Valid options:
     * "default": Use the built in defaults.
     * "custom": Call a custom function, defined by statusVisualOverrideFn().
     * @returns {string}
     */
    statusVisualOverride(): string {
        return "default";
    }

    /**
     * Expects a string array of current status values
     * @returns {null}
     */
    statusVisualOverrideFn(): Function {
        return null;
    }

    storageAdapter(): any {
        return null;
    }

    storageAllowCORS(): boolean {
        return true;
    }

    storageCallbackAfterLoad(): Function {
        return null;
    }

    storageCallbackAfterSave(): Function {
        return null;
    }

    storageGzipRequestBody(): boolean {
        return false;
    }

    storageIncludeScoDetails(): boolean {
        return false;
    }

    /**
     * If true, any commit that comes in while a storage save call is currently active will be blocked until
     * the previous call finishes, errors or times out.
     */
    storageQueueSaves(): boolean {
        return false;
    }

    /**
     * If the data is not substantially different than the previous time it was saved (ignore timestamps),
     * send it again.
     */
    storageSaveDuplicateCommits(): boolean {
        return true;
    }

    storageSaveFrequency(): number {
        return 10000;
    }

    storageTimeout(): number {
        return 30000;
    }

    storageUseFetch(): boolean {
        try {
            return "keepalive" in new Request("");
        } catch (e) {
            return false;
        }
    }

    storageUseSaveWorker(): boolean {
        try {
            return "Worker" in window;
        } catch (e) {
            return false;
        }
    }

    storageUseWithCredentials(): boolean {
        return true;
    }

    studentId(): string {
        return UrlVarService.get("studentId");
    }

    studentName(): string {
        return UrlVarService.get("studentName");
    }

    validateDataModelLength(): boolean {
        return true;
    }

    xapiActor(): string {
        return UrlVarService.get("actor");
    }

    xapiAuth(): string {
        return UrlVarService.get("auth");
    }

    xapiEndpoint(): string {
        return UrlVarService.get("endpoint");
    }

    xapiManifestFilename(): string {
        return "tincan.xml";
    }

    /**
     * If set, adds a registration parameter to the XAPI launch URL with whatever value is provided in the config.
     * @returns {string}
     */
    xapiRegistration(): string {
        return UrlVarService.get("registration");
    }
}


type ConfigCallable = (() => boolean) | (() => string);
interface SSLAWindow extends Window {
    sslaConfig: { [key: string]: ConfigCallable | string | boolean };
}
declare var window: SSLAWindow;


export class SSLAConfiguration implements IConfiguration {
    protected internalConfig: IConfigurationIndex;
    protected externalConfigSource: IConfigurationIndex;

    constructor() {
        this.internalConfig = new BasicConfiguration();
    }

    public _updateExternalConfigSource(externalConfigSource: IConfigurationIndex) {
        this.externalConfigSource = externalConfigSource;
    }

    protected isFunction(obj: any): obj is ConfigCallable {
        return !!(obj && obj.constructor && obj.call && obj.apply);
    }

    protected fromExt(fn: string, args: any[]=null): any {
        if (typeof this.externalConfigSource !== "undefined" && typeof this.externalConfigSource[fn] !== "undefined") {
            // Is it a function or a value?
            if (this.isFunction(this.externalConfigSource[fn])) {
                // This is necessary for IE7 as part of the bind polyfill, do not delete.
                if (!args) {
                    args = [];
                }
                return (<any>this.externalConfigSource)[fn](...args);
            }
            return this.externalConfigSource[fn];
        }
        if (typeof window.sslaConfig !== "undefined" && typeof window.sslaConfig[fn] !== "undefined") {
            // Is it a function or a value?
            if (this.isFunction(window.sslaConfig[fn])) {
                // This is necessary for IE7 as part of the bind polyfill, do not delete.
                if (!args) {
                    args = [];
                }
                return (<any>window.sslaConfig)[fn](...args);
            }
            return window.sslaConfig[fn];
        }
        // This is necessary for IE7 as part of the bind polyfill, do not delete.
        if (!args) {
            args = [];
        }
        return this.internalConfig[fn](...args);
    }

    aiccAuFilename(): string {
        return this.fromExt("aiccAuFilename");
    }

    aiccCourseFilename(): string {
        return this.fromExt("aiccCourseFilename");
    }

    aiccDescriptorFilename(): string {
        return this.fromExt("aiccDescriptorFilename");
    }

    aiccEndpoint(): string {
        return this.fromExt("aiccEndpoint");
    }

    aiccLoadRelativeToAu(): boolean {
        return this.fromExt("aiccLoadRelativeToAu");
    }

    aiccRawAu(): string {
        return this.fromExt("aiccRawAu");
    }

    aiccRawCourse(): string {
        return this.fromExt("aiccRawCourse");
    }

    aiccRawDescriptor(): string {
        return this.fromExt("aiccRawDescriptor");
    }

    aiccRawStructure(): string {
        return this.fromExt("aiccRawStructure");
    }

    aiccSessionId(systemId: string): string {
        return this.fromExt("aiccSessionId", [systemId]);
    }

    aiccStructureFilename(): string {
        return this.fromExt("aiccStructureFilename");
    }

    autoLaunchFirstSco(): boolean {
        return this.fromExt("autoLaunchFirstSco");
    }

    closePopupSingleScoBehavior(): string {
        return this.fromExt("closePopupSingleScoBehavior");
    }

    closePopupMultiScoBehavior(): string {
        return this.fromExt("closePopupMultiScoBehavior");
    }

    closePopupSingleScoCustomFunction(): any {
        return this.fromExt("closePopupSingleScoCustomFunction");
    }

    closePopupMultiScoCustomFunction(): any {
        return this.fromExt("closePopupMultiScoCustomFunction");
    }

    cmi5ManifestFilename(): string {
        return this.fromExt("cmi5ManifestFilename");
    }

    commentsFromLMS(scoId: string): object[] {
        return this.fromExt("commentsFromLMS", [scoId]);
    }

    courseBaseHtmlPath(): string {
        return this.fromExt("courseBaseHtmlPath");
    }

    courseDirectory(): string {
        return this.fromExt("courseDirectory");
    }

    courseId(): string {
        return this.fromExt("courseId");
    }

    exitAction(): string {
        return this.fromExt("exitAction");
    }

    exitFn(): Function {
        return this.fromExt("exitFn");
    }

    exitUrl(): string {
        return this.fromExt("exitUrl");
    }

    frameDefaultUrl(): string {
        return this.fromExt("frameDefaultUrl");
    }

    frameId(): string {
        return this.fromExt("frameId");
    }

    frameInterstitialUrl(): string {
        return this.fromExt("frameInterstitialUrl");
    }

    getCredit(): string {
        return this.fromExt("getCredit");
    }

    getDataAjaxMethod(): string {
        return this.fromExt("getDataAjaxMethod");
    }

    getDataHeaders(): any {
        return this.fromExt("getDataHeaders");
    }

    getDataUrl(): string {
        return this.fromExt("getDataUrl");
    }

    getMode(): string {
        return this.fromExt("getMode");
    }

    initializeScoAtOpen(): boolean {
        return this.fromExt("initializeScoAtOpen");
    }

    launchDataAllowOverride(scoId: string): boolean {
        return this.fromExt("launchDataAllowOverride", [scoId]);
    }

    launchDataOverride(scoId: string): string {
        return this.fromExt("launchDataOverride", [scoId]);
    }

    layoutUpdater(): Function {
        return this.fromExt("layoutUpdater");
    }

    learningSpecification(): string {
        return this.fromExt("learningSpecification");
    }

    logMessageBehavior(): string {
        return this.fromExt("logMessageBehavior");
    }

    logMessageCustomFunction(): any {
        return this.fromExt("logMessageCustomFunction");
    }

    looseIdentifiers(): boolean {
        return this.fromExt("looseIdentifiers");
    }

    masteryScoreMode(): string {
        return this.fromExt("masteryScoreMode");
    }

    modeBrowseAllowSave(): boolean {
        return this.fromExt("modeBrowseAllowSave");
    }

    modeReviewAllowSave(): boolean {
        return this.fromExt("modeReviewAllowSave");
    }

    multiScoView(): string {
        return this.fromExt("multiScoView");
    }

    navigationType(): string {
        return this.fromExt("navigationType");
    }

    openContentIn(): string {
        return this.fromExt("openContentIn");
    }

    popupMainContentMessageAfterOpen(): string {
        return this.fromExt("popupMainContentMessageAfterOpen");
    }

    popupMainContentMessageFailed(): string {
        return this.fromExt("popupMainContentMessageFailed");
    }

    popupName(): string {
        return this.fromExt("popupName");
    }

    popupWindowParams(): string {
        return this.fromExt("popupWindowParams");
    }

    reenterActivity(): boolean {
        return this.fromExt("reenterActivity");
    }

    returnToLastActivityOnLaunch(): boolean {
        return this.fromExt("returnToLastActivityOnLaunch");
    }

    saveDataOnCommit(): boolean {
        return this.fromExt("saveDataOnCommit");
    }

    saveDataOnSetValue(): boolean {
        return this.fromExt("saveDataOnSetValue");
    }

    scoreAllowChangeAfterCompleted(): boolean {
        return this.fromExt("scoreAllowChangeAfterCompleted");
    }

    scoreAllowChangeAfterFailed(): boolean {
        return this.fromExt("scoreAllowChangeAfterFailed");
    }

    scoreAllowChangeAfterPassed(): boolean {
        return this.fromExt("scoreAllowChangeAfterPassed");
    }

    scoreAllowReduce(): boolean {
        return this.fromExt("scoreAllowReduce");
    }

    scoreConvertNormalizedToRawMethod(): string {
        return this.fromExt("scoreConvertNormalizedToRawMethod");
    }

    scoreConvertRawToNormalizedMethod(): string {
        return this.fromExt("scoreConvertRawToNormalizedMethod");
    }

    scoreRollupAction(): string {
        return this.fromExt("scoreRollupAction");
    }

    scoreRollupCustomFunction(): any {
        return this.fromExt("scoreRollupCustomFunction");
    }

    scoreRollupIncludeScosWithNoScore(): boolean {
        return this.fromExt("scoreRollupIncludeScosWithNoScore");
    }

    scorm2004Mode(): string {
        return this.fromExt("scorm2004Mode");
    }

    scormManifestFilename(): string {
        return this.fromExt("scormManifestFilename");
    }

    scormManifestRawXml(): string {
        return this.fromExt("scormManifestRawXml");
    }

    scormManifestUseRawXml(): boolean {
        return this.fromExt("scormManifestUseRawXml");
    }

    setDataAjaxMethod(): string {
        return this.fromExt("setDataAjaxMethod");
    }

    setDataHeaders(): any {
        return this.fromExt("setDataHeaders");
    }

    setDataUrl(): string {
        return this.fromExt("setDataUrl");
    }

    singleScoView(): string {
        return this.fromExt("singleScoView");
    }

    startIncomplete(): boolean {
        return this.fromExt("startIncomplete");
    }

    statusAllowChangeAfterCompleted(): boolean {
        return this.fromExt("statusAllowChangeAfterCompleted");
    }

    statusAllowChangeAfterFailed(): boolean {
        return this.fromExt("statusAllowChangeAfterFailed");
    }

    statusAllowChangeAfterPassed(): boolean {
        return this.fromExt("statusAllowChangeAfterPassed");
    }

    statusAllowCompletionOnUnsetFinish(): boolean {
        return this.fromExt("statusAllowCompletionOnUnsetFinish");
    }

    statusRollupAction(): string {
        return this.fromExt("statusRollupAction");
    }

    statusRollupCustomFunction(): any {
        return this.fromExt("statusRollupCustomFunction");
    }

    statusVisualOverride(): string {
        return this.fromExt("statusVisualOverride");
    }

    statusVisualOverrideFn(): Function {
        return this.fromExt("statusVisualOverrideFn");
    }

    storageAdapter(): any {
        return this.fromExt("storageAdapter");
    }

    storageAllowCORS(): boolean {
        return this.fromExt("storageAllowCORS");
    }

    storageCallbackAfterLoad(): Function {
        return this.fromExt("storageCallbackAfterLoad");
    }

    storageCallbackAfterSave(): Function {
        return this.fromExt("storageCallbackAfterSave");
    }

    storageGzipRequestBody(): boolean {
        return this.fromExt("storageGzipRequestBody");
    }

    storageIncludeScoDetails(): boolean {
        return this.fromExt("storageIncludeScoDetails");
    }

    storageQueueSaves(): boolean {
        return this.fromExt("storageQueueSaves");
    }

    storageSaveDuplicateCommits(): boolean {
        return this.fromExt("storageSaveDuplicateCommits");
    }

    storageSaveFrequency(): number {
        return this.fromExt("storageSaveFrequency");
    }

    storageTimeout(): number {
        return this.fromExt("storageTimeout");
    }

    storageUseFetch(): boolean {
        return this.fromExt("storageUseFetch");
    }

    storageUseSaveWorker(): boolean {
        return this.fromExt("storageUseSaveWorker");
    }

    storageUseWithCredentials(): boolean {
        return this.fromExt("storageUseWithCredentials");
    }

    studentId(): string {
        return this.fromExt("studentId");
    }

    studentName(): string {
        return this.fromExt("studentName");
    }

    validateDataModelLength(): boolean {
        return this.fromExt("validateDataModelLength");
    }

    xapiActor(): string {
        return this.fromExt("xapiActor");
    }

    xapiAuth(): string {
        return this.fromExt("xapiAuth");
    }

    xapiEndpoint(): string {
        return this.fromExt("xapiEndpoint");
    }

    xapiManifestFilename(): string {
        return this.fromExt("xapiManifestFilename");
    }

    xapiRegistration(): string {
        return this.fromExt("xapiRegistration");
    }
}
